/**
 * Thin fetch wrapper for the /public/brain/* endpoints.
 *
 * Soft auth via X-Owl-Session header – read from localStorage on every
 * call so signing in/out doesn't require a page reload to take effect.
 *
 * If a fetch fails (network, 429, 5xx) the caller handles it; we don't
 * swallow errors here so the UI can show a real message.
 */
import { readSession, clearSession } from "./session";
import type {
  BriefingResponse,
  CalibrationResponse,
  DashboardResponse,
  FundCompareResponse,
  FundSearchResponse,
  HouseReadResponse,
  MarketChatResponse,
  OwlDailyResponse,
  OwlFeedResponse,
  OwlDailyBriefResponse,
  FromNyvoResponse,
} from "./types";

// Injected at build time by vite.config.ts. Fallback to dev defaults.
declare const __OWL_API_BASE__: string;
declare const __OWL_JARVIS_BASE__: string;

// In production Owl is served from the same nginx as the /public/brain/*
// endpoints (https://owl.nyvo.money), so the build sets API_BASE to the
// SAME origin – relative-equivalent, no CORS preflight. In dev we
// override to http://localhost:8000 because the Vite dev server runs
// on :5173 and FastAPI on :8000.
export const API_BASE: string =
  typeof __OWL_API_BASE__ !== "undefined" && __OWL_API_BASE__
    ? __OWL_API_BASE__
    : "http://localhost:8000";

export const JARVIS_BASE: string =
  typeof __OWL_JARVIS_BASE__ !== "undefined" && __OWL_JARVIS_BASE__
    ? __OWL_JARVIS_BASE__
    : "http://localhost:3000";

class ApiError extends Error {
  status: number;
  retryAfter?: number;
  constructor(status: number, message: string, retryAfter?: number) {
    super(message);
    this.status = status;
    this.retryAfter = retryAfter;
  }
}

function sessionHeaders(): Record<string, string> {
  const sess = readSession();
  return sess ? { "X-Owl-Session": sess.token } : {};
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const retryAfter = Number(res.headers.get("Retry-After")) || undefined;
    let detail = res.statusText;
    try {
      const j = await res.json();
      detail = j.detail || j.message || detail;
    } catch {
      /* response wasn't JSON – keep the statusText */
    }
    throw new ApiError(res.status, detail, retryAfter);
  }
  // 204 No Content has no body to parse.
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      ...sessionHeaders(),
    },
    body: JSON.stringify(body),
  });
  return handleResponse<T>(res);
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "GET",
    mode: "cors",
    headers: sessionHeaders(),
  });
  return handleResponse<T>(res);
}

export interface IdentifyResponse {
  token: string;
  email: string;
  query_count: number;
  new_user: boolean;
}

export interface MeResponse {
  signed_in: boolean;
  email?: string;
  query_count?: number;
  briefing_views?: number;
  first_seen_at?: string;
  // 2026-05-27 (v9.2) – surface waitlist signup state so the chat
  // drawer can skip the post-3rd-answer nudge for users who already
  // joined. Defaults to false on /me when anonymous.
  waitlist_joined?: boolean;
}

export interface ChatResponseWithId extends MarketChatResponse {
  query_id?: number;
}

export interface ChatHistoryMessage {
  role: "user" | "assistant";
  content: string;
}

export interface ChatHistoryResponse {
  messages: ChatHistoryMessage[];
}

export const OwlApi = {
  health() {
    return get<{ ok: boolean; service: string }>("/public/brain/health");
  },
  briefing(maxItems = 8) {
    return post<BriefingResponse>("/public/brain/briefing", {
      max_items: maxItems,
    });
  },
  daily() {
    return get<OwlDailyResponse>("/public/brain/daily");
  },
  /** The ranked, customer-value daily brief. Kept for the app and chat. */
  dailyBrief() {
    return get<OwlDailyBriefResponse>("/public/brain/daily-brief");
  },
  /** Owl's home surface (7 Aug 2026, replaced the daily cards): latest NYVO
   *  Weekly issue + earlier issues + tool boxes, synced from nyvo.money. */
  fromNyvo() {
    return get<FromNyvoResponse>("/public/brain/from-nyvo");
  },
  /** The older witty bento-card feed. No longer on the home page; kept because
   *  the endpoint still serves other readers. */
  feed() {
    return get<OwlFeedResponse>("/public/brain/feed");
  },
  dashboard() {
    return get<DashboardResponse>("/public/brain/dashboard");
  },
  calibration() {
    return get<CalibrationResponse>("/public/brain/calibration");
  },
  /**
   * Phase 3 (2026-07-02) – the customer-safe house read. Brainiac's directional
   * ideas (market stance + per-segment reads) in plain words, confidence-framed.
   * available=false on cold start; the section suppresses itself.
   */
  houseRead() {
    return get<HouseReadResponse>("/public/brain/house-read");
  },
  /**
   * 2026-05-27 (v9.2) – `localHistory` is an optional client-side
   * conversation buffer used for ANONYMOUS users. Signed-in users
   * get authoritative history from the server (owl_queries) and can
   * pass undefined here. Sending both is harmless – the server only
   * reads local_history when there's no signed-in email.
   */
  chat(question: string, localHistory?: ChatHistoryMessage[]) {
    return post<ChatResponseWithId>("/public/brain/market-chat", {
      question,
      // 2026-07-10 — we render blocks[] (BlockRenderer). Declaring it lets the
      // server skip the Claude prose for fund answers (10-14s -> 0.3s). Clients
      // that cannot render blocks must NOT send this: they get the prose.
      supports_blocks: true,
      ...(localHistory && localHistory.length > 0
        ? { local_history: localHistory }
        : {}),
    });
  },
  /**
   * 2026-07-02 (P4) – streaming chat. POSTs to /market-chat/stream and
   * parses SSE frames off the fetch body:
   *   event: delta  → onText(full scrubbed text-so-far)  [replace, not append]
   *   event: done   → resolves with the FULL sync-shaped response (query_id etc.)
   *   event: error  → throws ApiError
   * Callers treat streamed text as provisional and render the `done`
   * payload as authoritative. On any transport failure the caller falls
   * back to OwlApi.chat().
   */
  async chatStream(
    question: string,
    localHistory: ChatHistoryMessage[] | undefined,
    onText?: (text: string) => void,
  ): Promise<ChatResponseWithId> {
    const res = await fetch(`${API_BASE}/public/brain/market-chat/stream`, {
      method: "POST",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
        ...sessionHeaders(),
      },
      body: JSON.stringify({
        question,
        supports_blocks: true,        // see OwlApi.chat() above
        ...(localHistory && localHistory.length > 0
          ? { local_history: localHistory }
          : {}),
      }),
    });
    if (!res.ok || !res.body) {
      const retryAfter = Number(res.headers.get("Retry-After")) || undefined;
      let detail = res.statusText;
      try {
        const j = await res.json();
        detail = j.detail || j.message || detail;
      } catch { /* keep statusText */ }
      throw new ApiError(res.status, detail, retryAfter);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    let final: ChatResponseWithId | null = null;
    for (;;) {
      const { value, done: eof } = await reader.read();
      if (eof) break;
      buf += decoder.decode(value, { stream: true });
      let idx: number;
      while ((idx = buf.indexOf("\n\n")) !== -1) {
        const rawEvent = buf.slice(0, idx);
        buf = buf.slice(idx + 2);
        let event = "message";
        let data = "";
        for (const line of rawEvent.split("\n")) {
          if (line.startsWith("event: ")) event = line.slice(7).trim();
          else if (line.startsWith("data: ")) data += line.slice(6);
        }
        if (!data) continue;
        if (event === "delta") {
          try {
            const t = (JSON.parse(data) as { text?: string }).text;
            if (typeof t === "string") onText?.(t);
          } catch { /* skip a malformed frame – done is authoritative */ }
        } else if (event === "done") {
          // done is authoritative and terminal — return immediately.
          // (Review 2026-07-02: reading on after `done` meant an unclean
          // connection close could throw AWAY a fully-parsed answer and
          // trigger a duplicate sync re-send.)
          final = JSON.parse(data) as ChatResponseWithId;
          try { void reader.cancel(); } catch { /* already closed */ }
          return final;
        } else if (event === "error") {
          let message = "Owl's stream broke mid-answer.";
          try {
            message = (JSON.parse(data) as { message?: string }).message ?? message;
          } catch { /* keep default */ }
          throw new ApiError(502, message);
        }
      }
    }
    if (!final) throw new ApiError(502, "Stream ended without a final answer.");
    return final;
  },
  /**
   * 2026-07-02 (P4) – fund typeahead for the compare tool. ≥3 chars;
   * rated universe only.
   */
  fundSearch(q: string) {
    return get<FundSearchResponse>(
      `/public/brain/fund-search?q=${encodeURIComponent(q)}`,
    );
  },
  /** 2026-07-02 (P4) – factual 2-fund compare grid payload. */
  fundCompare(a: number, b: number) {
    return get<FundCompareResponse>(
      `/public/brain/fund-compare?a=${a}&b=${b}`,
    );
  },
  /**
   * 2026-05-27 (v9.2) – prior chat thread for the signed-in user,
   * oldest → newest. Anonymous callers get { messages: [] }.
   * Powers the ChatDrawer pre-fill so returning visitors see their
   * earlier conversation instead of an empty drawer.
   */
  chatHistory() {
    return get<ChatHistoryResponse>("/public/brain/chat-history");
  },
  identify(email: string) {
    return post<IdentifyResponse>("/public/brain/identify", { email });
  },
  me() {
    return get<MeResponse>("/public/brain/me");
  },
  /** Server-side invalidates the token; we also clear local state. */
  async signOut(): Promise<void> {
    try {
      await post<void>("/public/brain/sign-out", {});
    } finally {
      clearSession();
    }
  },
  feedback(queryId: number, thumbs: 1 | -1) {
    return post<void>("/public/brain/feedback", {
      query_id: queryId,
      thumbs,
    });
  },
  waitlist(payload: WaitlistPayload) {
    return post<WaitlistResponse>("/public/brain/waitlist", payload);
  },
  /**
   * 2026-05-28 – pairs with the silent default opt-in on /identify.
   * Called from the chat-drawer transparent confirmation card when
   * an auto-enrolled user clicks "Opt out". Requires a signed-in
   * session; anonymous users can't have been auto-enrolled.
   */
  waitlistOptOut() {
    return post<{ ok: boolean; email: string }>(
      "/public/brain/waitlist/opt-out",
      {},
    );
  },
  /**
   * 2026-05-28 (Item 7 prep) – fire a fire-and-forget analytics
   * beacon. Roll-our-own analytics so we don't ship customer
   * data to a third party. The backend rate-limits and validates
   * event_type / section; bad payloads are silently dropped.
   *
   * Returns a Promise but callers should NOT await – let it run
   * in the background. Catches errors so a beacon failure can't
   * break the page.
   */
  event(eventType: string, section?: string | null, value?: Record<string, unknown>) {
    return post<{ logged: boolean; event_id: number }>(
      "/public/brain/event",
      { event_type: eventType, section: section ?? null, value: value ?? null },
    ).catch(() => {
      // Never let an analytics failure surface – the user doesn't care
      // and we don't want a network blip to noisy-up the console.
      return { logged: false, event_id: 0 };
    });
  },
};

export type WaitlistSource = "modal_checkbox" | "section_form" | "chat_nudge";

export interface WaitlistPayload {
  email?: string;
  source: WaitlistSource;
  name?: string;
  phone?: string;
  goal?: string;
}

export interface WaitlistResponse {
  ok: boolean;
  already_on_list: boolean;
  position: number;
}

export { ApiError };
